// src/pages/materias/laboral/LaboralExpedienteDetallePage.jsx
import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { notify } from "../../components/ui/notify";
import {
  Row,
  Col,
  Typography,
  Tag,
  Space,
  Button,
  Breadcrumb,
  Card,
  Tabs,
  Modal,
  ConfigProvider,
} from "antd";
import { showInsetEffect } from "../../utils/antdInsetWave";
// Iconografía: bootstrap-icons (<i className="bi bi-...">) en vez de
// @ant-design/icons, para que todo el módulo use el mismo set de íconos
// que el resto de la app.
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";

import AudienciaPrejudicialCard from "../../components/conciliacion/AudienciaPrejudicialCard";
import DocumentosCard from "../../components/conciliacion/DocumentosCard";
import MovimientosCard from "../../components/conciliacion/Movimientos";
import CuantificacionPrejudicialCard from "../../components/conciliacion/CuantificacionPrejudicialCard";

import {
  actionConciliacionAudienciasGetByID,
  actionConciliacionDocumentosGet,
  actionConciliacionGetByID,
  actionAuditLogGet,
  actionConciliacionHistorialGetByID,
  actionConciliacionDocumentoCreate,
  actionConciliacionDocumentoDelete,
  actionConciliacionDelete,
  actionConciliacionUpdate,
} from "../../redux/actions/conciliacion/conciliacion";
import { actionAutoridadesGet } from "../../redux/actions/autoridades/autoridades";
import { actionObjetosGet } from "../../redux/actions/objetos/objetos";
import { actionMedioNotificacionGet } from "../../redux/actions/medio_notificacion/medio_notificacion";
import { actionEmpresasGet } from "../../redux/actions/empresas/empresas";
import useLaboralCatalogos from "./materias/laboral/useLaboralCatalogos";

import DatosExpedienteCard from "../../components/conciliacion/detalles/DatosExpedienteCard";
import EsquemaProcedimientoConciliacion from "../../components/conciliacion/detalles/EsquemaProcedimientoConciliacion";
import ChecklistPrejudicialCard from "../../components/conciliacion/detalles/ChecklistPrejudicialCard";
import DataBadge from "../../components/ui/DataBadge";
import { usePermisos } from "../../context/PermisosContext";

import {
  fmtDate,
  getRazonesSocialesText,
  getTrabajadoresText,
  getProximaAudiencia,
  getTipoLaboralLabel,
} from "../../components/conciliacion/detalles/laboralExpedienteFormatters";

import "../../components/conciliacion/detalles/LaboralExpedienteDetallePage.css";
import { de } from "date-fns/locale";


import {PATH} from "../../redux/utils"
const API_BASE = PATH;
const { Title, Text } = Typography;

const toTitleCase = (str) => {
  if (!str || typeof str !== "string") return "—";
  return str
    .toLowerCase()
    .split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

const MISSING_LABEL = "FALTA AGREGAR";

// Mismos colores/lógica que el badge de estatus usado en las tarjetas del
// listado (laboralConciliacionPage.jsx), para que el detalle luzca idéntico.
const STATUS_PILL_HEX = {
  green: "#16A34A",
  red: "#DC2626",
  blue: "#1677ff",
  yellow: "#F59E0B",
  gray: "#6B7280",
};

const getEstatusPill = (exp, audienciasCount) => {
  const rawStatus = String(exp?.status || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim();
  const isConstanciaConcl = Number(exp?.is_constancia_documento) === 1;

  if (isConstanciaConcl || rawStatus.includes("cumpl")) {
    return { label: "CUMPLIMIENTO", kind: "green" };
  }
  if (!isConstanciaConcl && rawStatus.includes("convenio")) {
    return { label: "CONVENIO", kind: "red" };
  }
  if (rawStatus.includes("activo")) {
    return { label: "1ERA AUDIENCIA", kind: "blue" };
  }
  if (rawStatus.includes("difer")) {
    const numAud = Number(
      audienciasCount ?? exp?.total_audiencias_prejudiciales ?? exp?.audiencias_count ?? exp?.num_audiencias ?? exp?.total_audiencias ?? exp?.audiencia_count ?? 0
    );
    const audNum = numAud >= 2 ? 3 : 2;
    return { label: `AUDIENCIA #${audNum}`, kind: "yellow" };
  }
  if (rawStatus.includes("no conciliac") || rawStatus.includes("no_conciliac")) {
    return { label: "NO CONCILIACIÓN", kind: "gray" };
  }
  if (rawStatus.includes("incompar") || rawStatus.includes("archivo")) {
    const quien = rawStatus.includes("patron")
      ? "Patrón"
      : (rawStatus.includes("trabajad") ? "Trabajador" : "");
    return { label: quien ? `ARCHIVO · ${quien.toUpperCase()}` : "ARCHIVO", kind: "gray" };
  }
  if (rawStatus.includes("conclu")) {
    return { label: "CONCLUIDO", kind: "gray" };
  }
  return { label: rawStatus.toUpperCase() || "SIN ESTADO", kind: "gray" };
};

// null/undefined/""/"   "/NaN/"—"
const isEmpty = (v) => {
  if (v === null || v === undefined) return true;
  if (typeof v === "number") return Number.isNaN(v);
  const s = String(v).trim();
  return s === "" || s === "—";
};

const MissingText = ({ inline = true }) => (
  <Text style={{ color: "#ad6800", fontWeight: 600, display: inline ? "inline" : "block" }}>
    {MISSING_LABEL}
  </Text>
);

// Devuelve texto normal o el placeholder amarillo
const showOrMissing = (value, renderValue) => {
  if (isEmpty(value)) return <MissingText />;
  return renderValue ? renderValue(value) : <Text>{String(value)}</Text>;
};
export default function LaboralExpedienteDetallePage({ maps = {} }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { idExpediente } = useParams();
  const location = useLocation();

  const { perm, canUseCity } = usePermisos() || { perm: () => true, canUseCity: () => true };

  const handleBack = () => {
    const state = location.state;
    if (state?.from === "home") {
      navigate("/home");
    } else {
      navigate(`/materias/laboral/centro-conciliacion${location.search}`);
    }
  };

const [activeMainTab, setActiveMainTab] = useState("1");
const [audienciaJump, setAudienciaJump] = useState(null);
const [deletingExp, setDeletingExp] = useState(false);

  const { documentos, detalle, audiencias } = useSelector(
    (state) => state.conciliacion
  );

  // La ciudad del expediente debe estar entre las ciudades permitidas al
  // usuario para el módulo Centro de Conciliación (ver columna "Ciudades"
  // en Usuarios > Permisos) para poder editarlo o eliminarlo.
  const puedeUsarCiudadExpediente = canUseCity("conciliacion", detalle?.id_ciudad);
  const canEditar = perm("conciliacion", "editar") && puedeUsarCiudadExpediente;
  const canEliminar = perm("conciliacion", "eliminar") && puedeUsarCiudadExpediente;

  // ============================================================
  // Edición inline (sin página aparte): catálogos para los selects
  // en cascada Estado -> Ciudad -> Autoridad, igual que en el
  // formulario de creación/edición de expediente.
  // ============================================================
  const [selectedEstadoId, setSelectedEstadoId] = useState(null);
  const [selectedCiudadId, setSelectedCiudadId] = useState(null);
  const [selectedAutoridadId, setSelectedAutoridadId] = useState(null);

  const {
    estadosOptions,
    ciudadesOptions: ciudadesOptionsAll,
    ciudadesById: ciudadesByIdCatalogo,
  } = useLaboralCatalogos(selectedEstadoId, selectedCiudadId, { modulo: "conciliacion" });

  const autoridadesSlice = useSelector((state) => state?.autoridades ?? {});
  const objetosSlice = useSelector((state) => state?.objetos ?? {});
  const empresasSlice = useSelector((state) => state?.empresas ?? {});

  useEffect(() => {
    dispatch(actionAutoridadesGet({}));
    dispatch(actionObjetosGet());
    dispatch(actionEmpresasGet({}));
  }, [dispatch]);

  const [medioNotifOptions, setMedioNotifOptions] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const result = await dispatch(actionMedioNotificacionGet());
        const items = Array.isArray(result)
          ? result
          : Array.isArray(result?.items)
          ? result.items
          : [];
        setMedioNotifOptions(
          items
            .filter((m) => m && m.id != null)
            .map((m) => ({ label: m.nombre || m.descripcion || `Medio ${m.id}`, value: m.id }))
        );
      } catch {
        setMedioNotifOptions([]);
      }
    })();
  }, [dispatch]);

  const coerceItems = (slice) => {
    if (!slice) return [];
    const data = slice.data ?? slice;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.items)) return data.items;
    return [];
  };

  const coerceEmpresasArray = (slice) => {
    if (Array.isArray(slice)) return slice;
    if (Array.isArray(slice?.items)) return slice.items;
    if (Array.isArray(slice?.data)) return slice.data;
    if (Array.isArray(slice?.list)) return slice.list;
    if (Array.isArray(slice?.items?.data)) return slice.items.data;
    if (Array.isArray(slice?.data?.items)) return slice.data.items;
    return [];
  };

  const autoridadesItems = useMemo(() => coerceItems(autoridadesSlice), [autoridadesSlice]);
  const objetosItems = useMemo(() => coerceItems(objetosSlice), [objetosSlice]);
  const empresasItems = useMemo(() => coerceEmpresasArray(empresasSlice), [empresasSlice]);

  const ciudadIdsConAutoridad = useMemo(() => {
    const s = new Set();
    autoridadesItems.forEach((au) => {
      if (au?.id_ciudad != null && String(au.id_ciudad) !== "") s.add(Number(au.id_ciudad));
    });
    return s;
  }, [autoridadesItems]);

  const estadoIdsConAutoridad = useMemo(() => {
    const s = new Set();
    ciudadIdsConAutoridad.forEach((idc) => {
      const c = ciudadesByIdCatalogo?.[idc] || ciudadesByIdCatalogo?.[String(idc)];
      const idEstado = c?.id_estado;
      if (idEstado != null) s.add(Number(idEstado));
    });
    return s;
  }, [ciudadIdsConAutoridad, ciudadesByIdCatalogo]);

  const estadosOptionsFinal = useMemo(
    () => (estadosOptions || []).filter((o) => estadoIdsConAutoridad.has(Number(o.value))),
    [estadosOptions, estadoIdsConAutoridad]
  );

  const ciudadesOptionsFinal = useMemo(() => {
    if (!selectedEstadoId) return [];
    return (ciudadesOptionsAll || []).filter((o) => ciudadIdsConAutoridad.has(Number(o.value)));
  }, [ciudadesOptionsAll, ciudadIdsConAutoridad, selectedEstadoId]);

  const autoridadOptions = useMemo(() => {
    if (!selectedCiudadId) return [];
    return autoridadesItems
      .filter((au) => Number(au?.id_ciudad || 0) === Number(selectedCiudadId))
      .map((au) => ({ label: au?.nombre || "", value: au?.id }))
      .filter((o) => o.label);
  }, [autoridadesItems, selectedCiudadId]);

  const objetosOptions = useMemo(
    () => objetosItems.map((o) => ({ label: o?.nombre || "", value: o?.id })).filter((o) => o.label),
    [objetosItems]
  );

  const razonesSocialesOptions = useMemo(() => {
    const map = new Map();
    empresasItems.forEach((it) => {
      const rsArr = Array.isArray(it?.razones_sociales) ? it.razones_sociales : [];
      rsArr.forEach((rs) => {
        const label = rs?.nombre ?? rs?.razon_social ?? "";
        const value = rs?.id_empresa_razon_social ?? rs?.id ?? null;
        if (label && value != null && !map.has(value)) map.set(value, { label, value });
      });
    });
    // Respaldo: garantiza que las razones sociales ya vinculadas a este
    // expediente siempre tengan una etiqueta legible, aunque el catálogo
    // de empresas aún no haya cargado o no las incluya.
    (Array.isArray(detalle?.razones_sociales) ? detalle.razones_sociales : []).forEach((rs) => {
      const value = rs?.id_razon_social ?? null;
      const label = rs?.razon_social ?? "";
      if (label && value != null && !map.has(value)) map.set(value, { label, value });
    });
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label, "es"));
  }, [empresasItems, detalle?.razones_sociales]);

  // ===== Edición inline: tarjeta "Datos del expediente" =====
  const [editingDatosCard, setEditingDatosCard] = useState(false);
  const [editingDatosForm, setEditingDatosForm] = useState({});
  const [savingDatosCard, setSavingDatosCard] = useState(false);

  // ===== Edición inline: tarjeta "Solicitantes" =====
  const [editingTrabajadoresCard, setEditingTrabajadoresCard] = useState(false);
  const [editingTrabajadoresForm, setEditingTrabajadoresForm] = useState({});
  const [savingTrabajadoresCard, setSavingTrabajadoresCard] = useState(false);

  // ===== Edición inline: tarjeta "Razones Sociales" =====
  const [editingRazonesCard, setEditingRazonesCard] = useState(false);
  const [editingRazonesForm, setEditingRazonesForm] = useState({});
  const [savingRazonesCard, setSavingRazonesCard] = useState(false);


const [exportingPDF, setExportingPDF] = useState(false);

// 1) carga inicial por expediente
useEffect(() => {
  dispatch(actionConciliacionDocumentosGet(idExpediente));
  dispatch(actionConciliacionGetByID(idExpediente));
  dispatch(actionConciliacionAudienciasGetByID(idExpediente));
  dispatch(actionConciliacionHistorialGetByID(idExpediente));
}, [dispatch, idExpediente]);

// 2) audit log depende de detalle.id (cuando ya existe)
useEffect(() => {
  if (!detalle?.id) return;
  dispatch(actionAuditLogGet({ id_modulo: 1, id_key: detalle.id, limit: 100 }));
}, [dispatch, detalle?.id]);


  const handleExportPDF = async () => {
    if (!detalle?.expediente_format) {
      notify.error({
        message: "Expediente no disponible",
        description: "No se encontró el expediente_format para exportar.",
      });
      return;
    }

    try {
      setExportingPDF(true);

      const token = localStorage.getItem("token");

      const res = await fetch(
        `${API_BASE}/conciliaciones/${detalle.expediente_format}/export-membrete`,
        {
          method: "GET",
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      if (!res.ok) {
        throw new Error("Error al generar el PDF");
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = `expediente_${
        detalle.expediente_format || detalle.expediente || "expediente"
      }.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      notify.success({
        message: "PDF generado",
        description: "El expediente se exportó correctamente.",
      });
    } catch (err) {
      console.error(err);
      notify.error({
        message: "Error al exportar",
        description: "No se pudo generar el PDF del expediente.",
      });
    } finally {
      setExportingPDF(false);
    }
  };

  if (!detalle) {
    return (
      <main className="laboral-exp-main">
        <div className="laboral-exp-content">
          <Card className="laboral-exp-summary-card p-2">
            <Text>Cargando expediente...</Text>
          </Card>
        </div>
      </main>
    );
  }

  const {
    ciudadesById = {},
    estadosById = {},
    abogadosById = {},
    ambitoById = { 1: "Local", 2: "Federal" },
  } = maps;

  const ciudadName =
    detalle.nombre_ciudad ||
    ciudadesById[detalle.id_ciudad]?.nombre ||
    ciudadesById[detalle.id_ciudad] ||
    (detalle.id_ciudad ? `#${detalle.id_ciudad}` : "—");

  const estadoName = (() => {
    if (detalle.nombre_estado) return detalle.nombre_estado;
    const ciudad = ciudadesById[detalle.id_ciudad];
    if (ciudad && ciudad.id_estado && estadosById[ciudad.id_estado]) {
      return (
        estadosById[ciudad.id_estado].nombre ||
        estadosById[ciudad.id_estado].code
      );
    }
    return "—";
  })();

  const ambitoName =
    detalle.nombre_comptencia ||
    ambitoById[detalle.id_competencia] ||
    (detalle.id_competencia ? `#${detalle.id_competencia}` : "—");

  const ObjetoName =
    detalle.nombre_objeto ||
    ambitoById[detalle.id_objeto] ||
    (detalle.id_objeto ? `#${detalle.id_objeto}` : "—");

  const estatusPill = getEstatusPill(detalle, Array.isArray(audiencias) ? audiencias.length : undefined);

  const abogadoName =
    detalle.nombre_abogado ||
    abogadosById[detalle.id_abogado]?.nombre ||
    abogadosById[detalle.id_abogado] ||
    (detalle.id_abogado ? `#${detalle.id_abogado}` : "—");

  const proximaAudiencia = getProximaAudiencia(detalle);
  const tipoLaboralLabel = getTipoLaboralLabel(detalle);
const handleAdelantarAudiencia = () => {
  setActiveMainTab("2");
  setAudienciaJump({ ts: Date.now(), type: "adelantar" });
  
};
const handleDeleteExpediente = () => {
  const idToDelete = detalle?.id; // ⚠️ cambia si tu PK real es otro (ej: detalle.id_conciliacion)

  if (!idToDelete) {
    notify.error({
      message: "No se puede eliminar",
      description: "No se encontró el idExpediente del expediente/conciliación.",
    });
    return;
  }

  Modal.confirm({
    title: "Eliminar expediente",
    content:
      "Esta acción elimina el expediente y su información relacionada.",
    okText: "Eliminar",
    okType: "danger",
    cancelText: "Cancelar",
    centered: true,
    okButtonProps: { loading: deletingExp, disabled: deletingExp },
    cancelButtonProps: { disabled: deletingExp },
    onOk: async () => {
      try {
        setDeletingExp(true);

        await dispatch(
          actionConciliacionDelete(idToDelete, () => {
            notify.success({
              message: "Expediente eliminado",
              description: "Se eliminó correctamente.",
            });
            handleBack();
          })
        );
      } catch (error) {
        const msg =
          error?.response?.data?.detail ||
          error?.message ||
          "No se pudo eliminar el expediente.";

        notify.error({
          message: "Error al eliminar",
          description: msg,
        });
      } finally {
        setDeletingExp(false);
      }
    },
  });
};

// ============================================================
// Edición inline — arma el payload completo que espera el PATCH
// de /conciliaciones/{id}. El backend recalcula el título/lugar
// de la audiencia en agenda a partir de trabajadores, razones
// sociales, autoridad y fecha, así que SIEMPRE hay que mandarlos
// completos (aunque la tarjeta que se edita sea otra), o si no el
// backend detecta un "cambio" donde no lo hubo y recrea el evento
// de agenda. Cada handleSave* solo pisa los campos que esa tarjeta
// realmente edita.
// ============================================================
const buildBasePayload = (overrides = {}) => {
  const trabajadoresBase =
    Array.isArray(detalle.trabajadores) && detalle.trabajadores.length
      ? detalle.trabajadores.map((t) => t.nombre_trabajador)
      : [];
  const rsIdsBase = Array.isArray(detalle.razones_sociales)
    ? detalle.razones_sociales.map((r) => r.id_razon_social).filter((v) => v != null)
    : [];
  const fechaAudienciaBase = detalle.fecha_proxima_audiencia || detalle.fecha_audiencia_inicial || null;

  return {
    expediente: detalle.expediente || null,
    fecha_emision_expediente: detalle.fecha_emision_expediente
      ? dayjs(detalle.fecha_emision_expediente).format("YYYY-MM-DD")
      : null,
    fecha_audiencia: fechaAudienciaBase ? dayjs(fechaAudienciaBase).format("YYYY-MM-DD HH:mm:ss") : null,
    abogado_contrario: detalle.abogado_contrario || "",
    trabajadores: trabajadoresBase,
    id_autoridad: detalle.id_autoridad ? Number(detalle.id_autoridad) : null,
    id_ciudad: detalle.id_ciudad ? Number(detalle.id_ciudad) : null,
    id_objeto: detalle.id_objeto ? Number(detalle.id_objeto) : null,
    tipo_notificado: detalle.tipo_notificado || null,
    tipo_notificado_actuario: detalle.tipo_notificado_actuario || null,
    fecha_notificacion: detalle.fecha_notificacion
      ? dayjs(detalle.fecha_notificacion).format("YYYY-MM-DD HH:mm:ss")
      : null,
    id_medio_notificacion: detalle.id_medio_notificacion ? Number(detalle.id_medio_notificacion) : null,
    razones_sociales_ids: rsIdsBase,
    ...overrides,
  };
};

const startEditDatosCard = () => {
  setSelectedEstadoId(detalle.id_estado ? Number(detalle.id_estado) : null);
  setSelectedCiudadId(detalle.id_ciudad ? Number(detalle.id_ciudad) : null);
  setSelectedAutoridadId(detalle.id_autoridad ? Number(detalle.id_autoridad) : null);
  const fechaAudienciaActual =
    detalle.fecha_cita_audiencia ||
    detalle.fecha_audiencia ||
    detalle.fecha_proxima_audiencia ||
    detalle.fecha_audiencia_inicial ||
    null;
  setEditingDatosForm({
    expediente: detalle.expediente || "",
    fecha_emision_expediente: detalle.fecha_emision_expediente
      ? dayjs(detalle.fecha_emision_expediente)
      : null,
    id_objeto: detalle.id_objeto ? Number(detalle.id_objeto) : null,
    tipo_notificado: detalle.tipo_notificado || null,
    tipo_notificado_actuario: detalle.tipo_notificado_actuario || null,
    fecha_notificacion: detalle.fecha_notificacion ? dayjs(detalle.fecha_notificacion) : null,
    id_medio_notificacion: detalle.id_medio_notificacion ? Number(detalle.id_medio_notificacion) : null,
    fecha_audiencia: fechaAudienciaActual ? dayjs(fechaAudienciaActual) : null,
  });
  setEditingDatosCard(true);
};

const handleSaveDatosCard = async () => {
  if (!selectedEstadoId || !selectedCiudadId || !selectedAutoridadId) {
    notify.error({
      message: "Faltan selecciones",
      description: "Selecciona Estado, Ciudad y Autoridad.",
      placement: "bottomRight",
    });
    return;
  }

  const esActuario = editingDatosForm.tipo_notificado === "actuario";

  if (!editingDatosForm.fecha_audiencia) {
    notify.error({
      message: "Falta la fecha de audiencia",
      description: "Selecciona la fecha y hora de la próxima audiencia.",
      placement: "bottomRight",
    });
    return;
  }

  setSavingDatosCard(true);
  try {
    const payload = buildBasePayload({
      expediente: (editingDatosForm.expediente || "").trim().toUpperCase() || null,
      fecha_emision_expediente: editingDatosForm.fecha_emision_expediente
        ? editingDatosForm.fecha_emision_expediente.format("YYYY-MM-DD")
        : null,
      id_autoridad: Number(selectedAutoridadId),
      id_ciudad: Number(selectedCiudadId),
      id_objeto: editingDatosForm.id_objeto ? Number(editingDatosForm.id_objeto) : null,
      tipo_notificado: editingDatosForm.tipo_notificado || null,
      tipo_notificado_actuario: editingDatosForm.tipo_notificado
        ? editingDatosForm.tipo_notificado_actuario || null
        : null,
      fecha_notificacion:
        esActuario && editingDatosForm.fecha_notificacion
          ? editingDatosForm.fecha_notificacion.format("YYYY-MM-DD HH:mm:ss")
          : null,
      id_medio_notificacion:
        !esActuario && editingDatosForm.id_medio_notificacion
          ? Number(editingDatosForm.id_medio_notificacion)
          : null,
      fecha_audiencia: editingDatosForm.fecha_audiencia.format("YYYY-MM-DD HH:mm:ss"),
    });

    await dispatch(actionConciliacionUpdate(detalle.id, payload));
    await dispatch(actionConciliacionGetByID(idExpediente));
    setEditingDatosCard(false);
    notify.success({
      message: "Expediente actualizado",
      description: "Los cambios se guardaron correctamente.",
      placement: "bottomRight",
    });
  } catch {
    // actionConciliacionUpdate ya muestra la notificación de error
  } finally {
    setSavingDatosCard(false);
  }
};

const startEditTrabajadoresCard = () => {
  const trabajadoresIniciales =
    Array.isArray(detalle.trabajadores) && detalle.trabajadores.length
      ? detalle.trabajadores.map((t) => t.nombre_trabajador)
      : [""];
  setEditingTrabajadoresForm({ trabajadores: trabajadoresIniciales });
  setEditingTrabajadoresCard(true);
};

const handleSaveTrabajadoresCard = async () => {
  const trabajadoresLimpios = (editingTrabajadoresForm.trabajadores || [])
    .map((n) => String(n || "").trim())
    .filter(Boolean);

  if (trabajadoresLimpios.length === 0) {
    notify.error({
      message: "Agrega al menos un trabajador",
      placement: "bottomRight",
    });
    return;
  }

  setSavingTrabajadoresCard(true);
  try {
    const payload = buildBasePayload({ trabajadores: trabajadoresLimpios });
    await dispatch(actionConciliacionUpdate(detalle.id, payload));
    await dispatch(actionConciliacionGetByID(idExpediente));
    setEditingTrabajadoresCard(false);
    notify.success({
      message: "Solicitantes actualizados",
      description: "Los cambios se guardaron correctamente.",
      placement: "bottomRight",
    });
  } catch {
    // actionConciliacionUpdate ya muestra la notificación de error
  } finally {
    setSavingTrabajadoresCard(false);
  }
};

const startEditRazonesCard = () => {
  // Se guarda {value, label} (no solo el id) tomando el nombre directo del
  // expediente: así el chip siempre muestra la razón social correcta desde
  // el primer render, sin depender de que el catálogo de empresas ya haya
  // cargado ni de que siga incluyendo esa razón social.
  const rsSelIniciales = Array.isArray(detalle.razones_sociales)
    ? detalle.razones_sociales
        .filter((r) => r?.id_razon_social != null)
        .map((r) => ({ value: r.id_razon_social, label: r.razon_social || `Razón social ${r.id_razon_social}` }))
    : [];
  setEditingRazonesForm({ razones_sociales_sel: rsSelIniciales });
  setEditingRazonesCard(true);
};

const handleSaveRazonesCard = async () => {
  setSavingRazonesCard(true);
  try {
    const razonesSocialesIds = (editingRazonesForm.razones_sociales_sel || [])
      .map((o) => o?.value)
      .filter((v) => v != null);
    const payload = buildBasePayload({ razones_sociales_ids: razonesSocialesIds });
    await dispatch(actionConciliacionUpdate(detalle.id, payload));
    await dispatch(actionConciliacionGetByID(idExpediente));
    setEditingRazonesCard(false);
    notify.success({
      message: "Razones sociales actualizadas",
      description: "Los cambios se guardaron correctamente.",
      placement: "bottomRight",
    });
  } catch {
    // actionConciliacionUpdate ya muestra la notificación de error
  } finally {
    setSavingRazonesCard(false);
  }
};

  return (
    <ConfigProvider wave={{ showEffect: showInsetEffect }}>
    <main className="laboral-exp-main">
      <div className="laboral-exp-content">
        <section className="laboral-exp-section">
          <Card className="laboral-exp-summary-card p-2">
            <Row align="middle" gutter={[16, 16]} justify="space-between" wrap>
              <Col flex="auto">
                <Space direction="vertical" size={8}>
                  <div className="laboral-exp-summary-title">
                    <Space align="center" wrap>
                      <Title
                        level={4}
                        style={{
                          margin: 0,
                          fontSize: 28,
                          fontWeight: 650,
                          letterSpacing: "-0.01em",
                          lineHeight: 1.2,
                          color: "#0f172a",
                        }}
                      >
                       {(getTrabajadoresText(detalle) || "").toUpperCase()} VS{" "}
                        {getRazonesSocialesText(detalle).toUpperCase()}
                      </Title>
                      <DataBadge
                        color={STATUS_PILL_HEX[estatusPill.kind] || estatusPill.kind}
                        className="d-inline-flex align-items-center gap-1 status-pill-badge"
                      >
                        {estatusPill.label}
                      </DataBadge>
                    </Space>
                    <Text
                      type="secondary"
                      className="laboral-exp-subtitle"
                    >
                      Núm. de expediente: {detalle.expediente || "Sin número"}
                    </Text>
                  </div>

                  <Space size="large" wrap className="laboral-exp-summary-meta">
                    <Space className="laboral-exp-meta-item">
                      <i className="fa-solid fa-location-dot" />
                      <Text>
                          Creado:{" "}
                        {ciudadName}
                        {estadoName !== "—" ? `, ${estadoName}` : ""}
                      </Text>
                    </Space>
                    <Space className="laboral-exp-meta-item">
                      <i className="fa-solid fa-calendar-days" />
                      <Text>
                        Creado:{" "}
                        {fmtDate(
                          detalle.fecha_creacion_expediente ||
                            detalle.created_at
                        )}
                      </Text>
                    </Space>
                    <Space className="laboral-exp-meta-item">
                      <i className="fa-solid fa-address-card" />
                      <Text>Creado por: {toTitleCase(detalle.nombre_usuario)}</Text>
                    </Space>
                  </Space>
                </Space>
              </Col>

              <Col flex="none">
                <Space>
                  {canEliminar && (
                  <Button
                    danger
                    type="primary"
                    icon={<i className="fa-solid fa-trash" />}
                    style={{ borderRadius: 8 }}
                    onClick={handleDeleteExpediente}
                    loading={deletingExp}
                    disabled={deletingExp}
                  >
                    Eliminar
                  </Button>
                  )}
                </Space>
              </Col>
            </Row>

            <div className="laboral-exp-header-tabs">
              {[
                { key: "1", label: "Principal", icon: <i className="fa-solid fa-file" /> },
                { key: "2", label: "Audiencia Prejudicial", icon: <i className="fa-solid fa-calendar-check" /> },
                { key: "4", label: "Documentos", icon: <i className="fa-solid fa-folder-open" /> },
                { key: "5", label: "Cuantificaciones", icon: <i className="fa-solid fa-calculator" /> },
                { key: "6", label: "Actividad", icon: <i className="fa-solid fa-list" /> },
              ].map((t) => (
                <button
                  key={t.key}
                  type="button"
                  className={`laboral-exp-tab-btn ${activeMainTab === t.key ? "laboral-exp-tab-btn-active" : ""}`}
                  onClick={() => setActiveMainTab(t.key)}
                >
                  {t.icon}
                  {t.label}
                </button>
              ))}
            </div>
          </Card>
        </section>

        {activeMainTab === "1" && (
          <>
            <section className="laboral-exp-section">
              <EsquemaProcedimientoConciliacion detalle={detalle} audiencias={audiencias} />
            </section>

            <section className="laboral-exp-section">
              <DatosExpedienteCard
                canEditar={canEditar}
                audiencias={audiencias}
                detalle={detalle}
                ObjetoName={ObjetoName}
                estadoName={estadoName}
                ciudadName={ciudadName}
                ambitoName={ambitoName}
                proximaAudiencia={proximaAudiencia}
                onExportPDF={handleExportPDF}
                exportingPDF={exportingPDF}
                onAdelantarAudiencia={handleAdelantarAudiencia}
                onDeleteExpediente={handleDeleteExpediente}
                deletingExp={deletingExp}
                // Edición inline — tarjeta "Datos del expediente"
                editingDatosCard={editingDatosCard}
                onStartEditDatos={startEditDatosCard}
                onCancelEditDatos={() => setEditingDatosCard(false)}
                onSaveDatos={handleSaveDatosCard}
                savingDatosCard={savingDatosCard}
                editingDatosForm={editingDatosForm}
                setEditingDatosForm={setEditingDatosForm}
                estadosOptionsFinal={estadosOptionsFinal}
                ciudadesOptionsFinal={ciudadesOptionsFinal}
                autoridadOptions={autoridadOptions}
                objetosOptions={objetosOptions}
                medioNotifOptions={medioNotifOptions}
                selectedEstadoId={selectedEstadoId}
                setSelectedEstadoId={setSelectedEstadoId}
                selectedCiudadId={selectedCiudadId}
                setSelectedCiudadId={setSelectedCiudadId}
                selectedAutoridadId={selectedAutoridadId}
                setSelectedAutoridadId={setSelectedAutoridadId}
                // Edición inline — tarjeta "Solicitantes"
                editingTrabajadoresCard={editingTrabajadoresCard}
                onStartEditTrabajadores={startEditTrabajadoresCard}
                onCancelEditTrabajadores={() => setEditingTrabajadoresCard(false)}
                onSaveTrabajadores={handleSaveTrabajadoresCard}
                savingTrabajadoresCard={savingTrabajadoresCard}
                editingTrabajadoresForm={editingTrabajadoresForm}
                setEditingTrabajadoresForm={setEditingTrabajadoresForm}

                editingRazonesCard={editingRazonesCard}
                onStartEditRazones={startEditRazonesCard}
                onCancelEditRazones={() => setEditingRazonesCard(false)}
                onSaveRazones={handleSaveRazonesCard}
                savingRazonesCard={savingRazonesCard}
                editingRazonesForm={editingRazonesForm}
                setEditingRazonesForm={setEditingRazonesForm}
                razonesSocialesOptions={razonesSocialesOptions}
              />
            </section>

            <section className="laboral-exp-section">
              <ChecklistPrejudicialCard idExpediente={idExpediente} />
            </section>
          </>
        )}

        {activeMainTab === "2" && (
          <AudienciaPrejudicialCard
            data={detalle}
            dataAudiencias={audiencias}
            jump={audienciaJump}
          />
        )}

        {activeMainTab === "4" && (
          <DocumentosCard
            data={documentos}
            dataDetalles={detalle}
            idExpediente={idExpediente}
            tiposDocumentos={documentos?.items?.tipos_documentos}
            onCreateDocumento={(payload) =>
              dispatch(actionConciliacionDocumentoCreate(payload))
            }
            onDeleteDocumento={(payload) =>
              dispatch(actionConciliacionDocumentoDelete(payload))
            }
          />
        )}

        {activeMainTab === "5" && (
          <CuantificacionPrejudicialCard data={detalle} />
        )}

        {activeMainTab === "6" && <MovimientosCard />}
      </div>
    </main>
    </ConfigProvider>
  );
}
